import React from 'react';
import { evaluateCondition } from '../fieldCondition';
import type { FieldCondition } from '../fieldCondition';

import { FactBoxTotals } from '../FactBoxTotals';
import { FactBoxCfde } from '../FactBoxCfde';
import { FactBoxPaymentTerms } from '../FactBoxPaymentTerms';
import { FactBoxFinancial } from '../FactBoxFinancial';
import { FactBoxStatusHistory } from '../FactBoxStatusHistory';
import { StateBadge } from '../StateBadge';
import type { StatusHistoryEntry } from '../adapter/MasterDocumentAdapter.types';

import type { FactBoxSectionConfig, DocumentLineData } from '../MasterDocument.types';
import type { DocumentState, IMasterDocumentAdapter } from '../adapter/MasterDocumentAdapter.types';
import type { FactBoxWidget } from '../provider/MasterDocumentProvider';
import { INERT_REFRESH, type MasterDocumentRefreshApi } from '../provider/MasterDocumentRefreshProvider';
import type {
  FactBoxSectionTemplate,
  TotalsWidgetConfig,
  TotalsAggregation,
  KeyValueWidgetConfig,
  ProgressBarWidgetConfig,
} from '../template/MasterDocumentTemplate.types';
import { MASTER_DOCUMENT_TRANSLATIONS_ES } from '../i18n';
import type { TranslateFn } from '../i18nFactory';

// ════════════════════════════════════════════════════════════
// DATA RESOLVER — obtiene datos de la fuente correcta
// ════════════════════════════════════════════════════════════

function resolveData(
  section: FactBoxSectionTemplate,
  state: DocumentState,
): Record<string, unknown> {
  if (section.dataSource === 'adapter') {
    return state.factBoxData[section.key] ?? {};
  }
  // documentState: merge header + computed totals
  return { ...state.headerValues, _lines: state.lines, _lineCount: state.lines.length };
}

function resolveField(data: Record<string, unknown>, field: string): unknown {
  return data[field];
}

// ════════════════════════════════════════════════════════════
// AGGREGATION ENGINE — calcula totales desde state.lines
// ════════════════════════════════════════════════════════════

function computeAggregations(
  aggregations: Record<string, TotalsAggregation>,
  lines: DocumentLineData[],
): Record<string, number> {
  const result: Record<string, number> = {};

  for (const [key, agg] of Object.entries(aggregations)) {
    switch (agg.op) {
      case 'sum':
        result[key] = lines.reduce((acc, l) => acc + (Number(l[agg.field]) || 0), 0);
        break;
      case 'sumProduct':
        result[key] = lines.reduce((acc, l) => {
          const product = agg.fields.reduce((p, f) => p * (Number(l[f]) || 0), 1);
          return acc + product;
        }, 0);
        break;
      case 'sumDiscount':
        result[key] = lines.reduce((acc, l) => {
          const qty = Number(l[agg.qtyField]) || 0;
          const price = Number(l[agg.priceField]) || 0;
          const discount = Number(l[agg.discountField]) || 0;
          return acc + qty * price * (1 - discount / 100);
        }, 0);
        break;
      case 'diff':
        result[key] = (result[agg.a] ?? 0) - (result[agg.b] ?? 0);
        break;
      case 'add':
        result[key] = agg.fields.reduce((acc, f) => acc + (result[f] ?? 0), 0);
        break;
      case 'ref':
        result[key] = (result[agg.field] ?? 0) * (agg.multiplier ?? 1);
        break;
    }
  }

  return result;
}

// ════════════════════════════════════════════════════════════
// WIDGET RENDERERS
// ════════════════════════════════════════════════════════════

function renderTotals(
  config: TotalsWidgetConfig,
  data: Record<string, unknown>,
  adapter: IMasterDocumentAdapter,
): React.ReactNode {
  // Calcular aggregations desde líneas si están configuradas
  const resolvedData: Record<string, unknown> = config.aggregations
    ? { ...data, ...computeAggregations(config.aggregations, (data._lines as DocumentLineData[]) ?? []) }
    : data;

  const fmtValue = (field: string, negated?: boolean) => {
    const v = Number(resolveField(resolvedData, field)) || 0;
    const formatted = adapter.formatCurrency(Math.abs(v));
    return negated ? `–${formatted}` : formatted;
  };

  const subtitle = config.subtitleTemplate
    ? config.subtitleTemplate.replace(
        /\{(\w+)\}/g,
        (_, key) => String(resolveField(resolvedData, key) ?? ''),
      )
    : undefined;

  return (
    <FactBoxTotals
      grandTotal={{
        label: config.grandTotalLabel,
        value: fmtValue(config.grandTotalField),
        subtitle,
      }}
      kpis={config.kpis.map((kpi) => ({
        label: kpi.label,
        value: fmtValue(kpi.field, kpi.negated),
        color: kpi.color,
      }))}
      breakdown={config.breakdown.map((item) => ({
        label: item.label,
        value: fmtValue(item.field, item.negated),
        color: item.color,
        isSummary: item.isSummary,
      }))}
    />
  );
}

function renderCfde(data: Record<string, unknown>): React.ReactNode {
  return (
    <FactBoxCfde
      data={{
        status: (data.status as 'PENDING' | 'SENT' | 'ACCEPTED' | 'REJECTED' | 'ERROR') ?? 'PENDING',
        number: data.number as string | undefined,
        cufe: data.cufe as string | undefined,
        issueDate: data.issueDate as string | undefined,
        qrUrl: data.qrUrl as string | undefined,
        printCount: data.printCount as number | undefined,
        isTransmitted: data.isTransmitted as boolean | undefined,
      }}
    />
  );
}

function renderPaymentTerms(data: Record<string, unknown>): React.ReactNode {
  return (
    <FactBoxPaymentTerms
      data={{
        code: String(data.code ?? ''),
        name: String(data.name ?? ''),
        installmentCount: Number(data.installmentCount) || 0,
        periodMode: (data.periodMode as 'days' | 'months' | 'custom') ?? 'days',
        dueDays: Number(data.dueDays) || 0,
        earlyPaymentEnabled: data.earlyPaymentEnabled as boolean | undefined,
        earlyPaymentDays: data.earlyPaymentDays as number | undefined,
        earlyPaymentDiscountPct: data.earlyPaymentDiscountPct as number | undefined,
        advancePaymentPct: data.advancePaymentPct as number | undefined,
        advancePaymentAmount: data.advancePaymentAmount as number | undefined,
      }}
    />
  );
}

function renderFinancial(
  data: Record<string, unknown>,
  adapter: IMasterDocumentAdapter,
): React.ReactNode {
  return (
    <FactBoxFinancial
      data={{
        receivableAmount: data.receivableAmount as number | undefined,
        payableAmount: data.payableAmount as number | undefined,
        advanceAmount: data.advanceAmount as number | undefined,
        cashAmount: data.cashAmount as number | undefined,
        receivableAmountLocal: data.receivableAmountLocal as number | undefined,
        payableAmountLocal: data.payableAmountLocal as number | undefined,
        currencyCode: data.currencyCode as string | undefined,
      }}
      formatCurrency={adapter.formatCurrency}
    />
  );
}

function renderKeyValue(
  config: KeyValueWidgetConfig,
  data: Record<string, unknown>,
  adapter: IMasterDocumentAdapter,
): React.ReactNode {
  return (
    <div className="space-y-1.5">
      {config.fields.map((f) => {
        const raw = resolveField(data, f.field);
        let display: string;
        switch (f.format) {
          case 'currency':
            display = adapter.formatCurrency(Number(raw) || 0);
            break;
          case 'date':
            display = adapter.formatDate ? adapter.formatDate(String(raw ?? '')) : String(raw ?? '—');
            break;
          case 'number':
            display = adapter.formatNumber ? adapter.formatNumber(Number(raw) || 0) : String(Number(raw) || 0);
            break;
          default:
            display = String(raw ?? '—');
        }
        return (
          <div key={f.field} className="flex justify-between text-xs py-0.5">
            <span className="text-content-secondary dark:text-dark-content-secondary">{f.label}</span>
            <span className="font-semibold tabular-nums text-content-primary dark:text-dark-content-primary">{display}</span>
          </div>
        );
      })}
    </div>
  );
}

function renderProgressBar(
  config: ProgressBarWidgetConfig,
  data: Record<string, unknown>,
): React.ReactNode {
  const value = Number(resolveField(data, config.valueField)) || 0;
  const max = Number(resolveField(data, config.maxField)) || 1;
  const pct = Math.min(Math.round((value / max) * 100), 100);

  return (
    <div className="space-y-1.5">
      <div className="flex justify-between text-xs text-content-secondary dark:text-dark-content-secondary">
        <span>{config.label}</span>
        <span className="font-semibold">{pct}%</span>
      </div>
      <div className="h-[5px] bg-background-secondary dark:bg-dark-bg-secondary rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-300 bg-green-700 dark:bg-green-500"
          style={{ width: `${pct}%`, backgroundColor: config.color ?? undefined }}
        />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// BUILD FACTBOX SECTIONS
// ════════════════════════════════════════════════════════════

export function buildFactBoxSections(
  sectionTemplates: FactBoxSectionTemplate[],
  state: DocumentState,
  adapter: IMasterDocumentAdapter,
  userPermissions: string[] | null = null,
  factBoxWidgets: Record<string, FactBoxWidget> = {},
  /**
   * Traductor del kit. Se recibe como parámetro porque esto es una función pura
   * llamada desde un `useMemo`, no un componente: no puede usar el hook.
   */
  t: TranslateFn = (key) => MASTER_DOCUMENT_TRANSLATIONS_ES[key] ?? key,
  /**
   * Resincronización del documento, que se entrega tal cual a los widgets custom.
   * Por defecto inerte: esta función también se usa desde pruebas y stories, y
   * ahí no hay reducer que recargar.
   */
  refresh: MasterDocumentRefreshApi = INERT_REFRESH,
): FactBoxSectionConfig[] {
  const headerValues = state.headerValues as Record<string, unknown>;
  return sectionTemplates
    .filter((s) => {
      if (s.visibleOn && s.visibleOn.length > 0) {
        if (!s.visibleOn.includes(state.docState) && !s.visibleOn.includes('*')) return false;
      }
      if (s.visibleWhen && !evaluateCondition(s.visibleWhen as FieldCondition, headerValues)) return false;
      if (s.hiddenWhen && evaluateCondition(s.hiddenWhen as FieldCondition, headerValues)) return false;
      if (s.permissionVisibility && userPermissions !== null && !userPermissions.includes(s.permissionVisibility)) return false;
      return true;
    })
    .map((s) => {
      const data = resolveData(s, state);

      // Resolve badge
      let badge: React.ReactNode = undefined;
      if (s.badgeField && s.badgeMap) {
        const badgeValue = String(data[s.badgeField] ?? '');
        const badgeConfig = s.badgeMap[badgeValue];
        if (badgeConfig) {
          badge = <StateBadge {...badgeConfig} />;
        }
      }

      // Render widget
      let children: React.ReactNode;
      switch (s.widget.type) {
        case 'totals':
          children = renderTotals(s.widget.config, data, adapter);
          break;
        case 'cfde':
          children = renderCfde(data);
          break;
        case 'paymentTerms':
          children = renderPaymentTerms(data);
          break;
        case 'financial':
          children = renderFinancial(data, adapter);
          break;
        case 'keyValue':
          children = renderKeyValue(s.widget.config, data, adapter);
          break;
        case 'progressBar':
          children = renderProgressBar(s.widget.config, data);
          break;
        case 'statusHistory': {
          const historyEntries = (data.entries ?? []) as StatusHistoryEntry[];
          children = (
            <FactBoxStatusHistory
              entries={historyEntries}
              formatDate={adapter.formatDate}
            />
          );
          break;
        }
        case 'custom': {
          const widget = factBoxWidgets[s.widget.widgetKey];
          if (typeof widget === 'function') {
            children = widget({
              documentId: state.documentId ?? '',
              docState: state.docState,
              mode: state.mode,
              headerValues,
              data,
              adapter,
              reload: refresh.reload,
              refreshToken: refresh.refreshToken,
            });
          } else if (widget != null && widget !== false) {
            children = widget;
          } else {
            children = (
              <div className="text-xs text-content-secondary dark:text-dark-content-secondary italic">
                {t('master_document.factBox.customWidget', { key: s.widget.widgetKey })}
              </div>
            );
          }
          break;
        }
      }

      return {
        key: s.key,
        label: s.label,
        defaultOpen: s.defaultOpen,
        badge,
        children,
      };
    });
}
